import { type ParentComponent, Show, untrack } from "solid-js";
import { Icon, type IconName } from "../icon/Icon";
import { Collapsible } from "@ark-ui/solid";
import { isNil } from "lodash-es";
import Text from "../text/Text";
import { condition } from "../../utils/solidUtils";
import { createWritableMemo } from "@solid-primitives/memo";
import type { Status } from "../../types";
import { STATUSES } from "../../types";

type InlineAccordionProps = {
  expandLabel: string;
  collapseLabel: string;
  icon?: IconName;
  open?: boolean;
  variant?: Status;
  setOpen?: () => boolean;
};

const getIsDefaultVariant = (variant?: string | null) => {
  if (isNil(variant)) {
    return true;
  }
  return !STATUSES.includes(variant);
};

const InlineAccordion: ParentComponent<InlineAccordionProps> = (props) => {
  const isDefaultVariant = getIsDefaultVariant(untrack(() => props.variant));
  const [open, setOpen] = createWritableMemo<boolean>(() => props.open ?? false);
  const textClasses = () => ({
    "text-button-text-tertiary-soft group-hover/trigger:text-button-text-tertiary-soft-hover group-active/trigger:text-button-text-tertiary-soft-pressed":
      isDefaultVariant,
    "text-button-text-status-warning group-hover/trigger:text-button-text-status-warning-hover group-active/trigger:text-button-text-status-warning-pressed":
      props.variant === "warning",
    "text-button-text-status-danger group-hover/trigger:text-button-text-status-danger-hover group-active/trigger:text-button-text-status-danger-pressed":
      props.variant === "danger",
    "text-button-text-status-informative group-hover/trigger:text-button-text-status-informative-hover group-active/trigger:text-button-text-status-informative-pressed":
      props.variant === "informative",
    "text-button-text-status-upgrade group-hover/trigger:text-button-text-status-upgrade-hover group-active/trigger:text-button-text-status-upgrade-pressed":
      props.variant === "upgrade",
  });
  const iconClasses = () => ({
    "icon-secondary group-hover/trigger:icon-button-icon-tertiary-soft-hover group-active/trigger:button-icon-tertiary-soft-pressed":
      isDefaultVariant,
    "icon-button-icon-status-warning group-hover/trigger:icon-button-icon-status-warning-hover group-active/trigger:icon-button-icon-status-warning-pressed":
      props.variant === "warning",
    "icon-button-icon-status-danger group-hover/trigger:icon-button-icon-status-danger-hover group-active/trigger:icon-button-icon-status-danger-pressed":
      props.variant === "danger",
    "icon-button-icon-status-informative group-hover/trigger:icon-button-icon-status-informative-hover group-active/trigger:icon-button-icon-status-informative-pressed":
      props.variant === "informative",
    "icon-button-icon-status-upgrade group-hover/trigger:icon-button-icon-status-upgrade-hover group-active/trigger:icon-button-icon-status-upgrade-pressed":
      props.variant === "upgrade",
  });

  return (
    <Collapsible.Root
      class="w-full"
      open={open()}
      onOpenChange={(details) => {
        if (isNil(props?.setOpen)) {
          setOpen(details.open);
        } else {
          setOpen(props.setOpen() === true);
        }
      }}
    >
      <Collapsible.Context>
        {(context) => (
          <>
            <Collapsible.Trigger class="group/trigger flex max-w-full items-center gap-1 text-small-medium focus-visible:outline-default">
              <Show when={condition(props.icon, (icon) => !isNil(icon))}>
                {(icon) => <Icon name={icon()} type="solid" size="sm" class="shrink-0" classList={iconClasses()} />}
              </Show>
              <Text
                as="span"
                truncate
                text={context().open ? props.collapseLabel : props.expandLabel}
                classList={textClasses()}
              />
              <div
                class="shrink-0 transition-transform duration-200 ease-linear"
                classList={{ "rotate-180": context().open }}
              >
                <Icon name="chevronDown" type="solid" size="sm" classList={iconClasses()} />
              </div>
            </Collapsible.Trigger>
            {/* oxlint-disable better-tailwindcss/no-restricted-classes */}
            <Collapsible.Content class="grid grid-rows-[1fr] transition-all duration-250 ease-out closed:grid-rows-[0fr] closed:duration-200 closed:ease-in">
              <div class="overflow-hidden">{props.children}</div>
            </Collapsible.Content>
          </>
        )}
      </Collapsible.Context>
    </Collapsible.Root>
  );
};

export default InlineAccordion;
